import { Quaternion, Vector3 } from "three";
import type { CONFIGURATOR_MODEL_TYPE } from "../../core/configurator/ConfiguratorData";
import type {
  ConfiguratorRuntimeDocument,
  RuntimeConfiguratorDocument,
  RuntimePartRef,
  RuntimeTransform,
} from "./ConfiguratorRuntimeDocument";
import { SugarModel } from "../../loader/SugarModel";
import { SugarModelConfigurator } from "../../loader/SugarModelConfigurator";
import { SugarModelGroup } from "../../loader/SugarModelGroup";
import { trackStateRoot, untrackStateRoot } from "../../history/SugarObject";

export class ConfiguratorRuntimeDocumentLoader {
  constructor(private readonly configData: CONFIGURATOR_MODEL_TYPE) {}

  async load(document: ConfiguratorRuntimeDocument): Promise<SugarModel[]> {
    if (document.version !== 1) {
      throw new Error(`Unsupported configurator document: ${document.version}`);
    }

    const configDocs = new Map(
      document.configurators.map((configurator) => [
        configurator.uuid,
        configurator,
      ]),
    );
    const parentConfigByChild = new Map<string, RuntimeConfiguratorDocument>();
    for (const configurator of document.configurators) {
      for (const childUuid of configurator.childUuids) {
        parentConfigByChild.set(childUuid, configurator);
      }
    }

    const objects = new Map<string, SugarModel>();
    await Promise.all(
      document.models.map(async (modelDoc) => {
        const data = this.configData.models.find(
          (model) => Number(model.id) === modelDoc.catalogModelId,
        );
        if (!data) {
          throw new Error(
            `Catalog model ${modelDoc.catalogModelId} not found`,
          );
        }
        const parentConfig = parentConfigByChild.get(modelDoc.uuid);
        const model = new SugarModelGroup(data, this.configData);
        replaceUuid(model, modelDoc.uuid);
        await model.load({
          data,
          partConfig: resolvePartConfig(
            parentConfig?.mainConfig ?? [],
            this.configData,
          ),
        });
        objects.set(modelDoc.uuid, model);
      }),
    );

    const buildConfigurator = (doc: RuntimeConfiguratorDocument) => {
      const existing = objects.get(doc.uuid);
      if (existing instanceof SugarModelConfigurator) return existing;
      const configurator = new SugarModelConfigurator(
        resolvePartConfig(doc.mainConfig, this.configData),
        resolvePartConfig(doc.sideConfig, this.configData),
        resolveMaterialConfig(doc, this.configData),
        this.configData,
      );
      replaceUuid(configurator, doc.uuid);
      objects.set(doc.uuid, configurator);
      for (const childUuid of doc.childUuids) {
        const childDoc = configDocs.get(childUuid);
        const child = childDoc
          ? buildConfigurator(childDoc)
          : objects.get(childUuid);
        if (!child) throw new Error(`Runtime child ${childUuid} not found`);
        configurator.addRootModel(child);
      }
      return configurator;
    };

    document.configurators.forEach(buildConfigurator);
    const roots = document.rootUuids.map((uuid) => {
      const object = objects.get(uuid);
      if (!object) throw new Error(`Runtime root ${uuid} not found`);
      return object;
    });

    for (const root of roots) root.updateMatrixWorld(true);
    for (const configDoc of document.configurators) {
      const configurator = objects.get(configDoc.uuid);
      if (!(configurator instanceof SugarModelConfigurator)) continue;
      setWorldTransform(configurator, configDoc.transform);
      configurator.updateMatrixWorld(true);
      for (const childUuid of configDoc.childUuids) {
        const child = objects.get(childUuid);
        const modelDoc = document.models.find((item) => item.uuid === childUuid);
        const childConfigDoc = configDocs.get(childUuid);
        if (child && (modelDoc || childConfigDoc)) {
          setWorldTransform(
            child,
            (modelDoc ?? childConfigDoc)!.transform,
          );
        }
      }
      restoreBindings(configurator, configDoc);
    }

    for (const configDoc of document.configurators) {
      const configurator = objects.get(configDoc.uuid);
      if (configurator instanceof SugarModelConfigurator) {
        await configurator.completeSync();
      }
    }

    for (const modelDoc of document.models) {
      const model = objects.get(modelDoc.uuid);
      if (!model) continue;
      setWorldTransform(model, modelDoc.transform);
      for (const materialState of modelDoc.partMaterials) {
        const group = model.partMaterialGroups?.find(
          (candidate) => candidate.code === materialState.code,
        );
        const material = group?.materials?.find(
          (candidate) => String(candidate.id) === materialState.materialId,
        );
        if (group && material) {
          group.material = material;
          group.apply(model);
        }
      }
    }
    roots.forEach((root) => root.updateMatrixWorld(true));
    return roots;
  }
}

function resolvePartConfig(
  refs: Array<[string, RuntimePartRef]>,
  configData: CONFIGURATOR_MODEL_TYPE,
) {
  const parts = [
    ...(configData.configurator.backs ?? []),
    ...(configData.configurator.legs ?? []),
    ...(configData.configurator.sides ?? []),
  ];
  return new Map(
    refs.map(([key, ref]) => {
      const part = parts.find(
        (candidate) =>
          candidate.type === ref.type && candidate.name === ref.name,
      );
      if (!part) throw new Error(`Configurator option not found: ${ref.name}`);
      return [key, part];
    }),
  );
}

function resolveMaterialConfig(
  document: RuntimeConfiguratorDocument,
  configData: CONFIGURATOR_MODEL_TYPE,
): Map<string, { code: string; material: any }> {
  const materials = configData.materialPartGroups.flatMap((group) =>
    group.materialGroups.flatMap((materialGroup) => materialGroup.materials),
  );
  return new Map(
    document.materialConfig.map(([key, value]) => [
      key,
      {
        code: value.code,
        material: materials.find(
          (material) =>
            String(material.id ?? material.uuid) === value.materialId,
        ),
      },
    ]),
  );
}

function replaceUuid(model: SugarModel, uuid: string): void {
  untrackStateRoot(model);
  (model as { uuid: string }).uuid = uuid;
  trackStateRoot(model);
}

function setWorldTransform(
  object: SugarModel,
  transform: RuntimeTransform,
): void {
  const worldPosition = new Vector3(...transform.p);
  const worldQuaternion = new Quaternion(...transform.q);
  const parent = object.parent;
  if (!parent) {
    object.position.copy(worldPosition);
    object.quaternion.copy(worldQuaternion);
  } else {
    parent.updateMatrixWorld(true);
    object.position.copy(parent.worldToLocal(worldPosition.clone()));
    const parentQuaternion = parent.getWorldQuaternion(new Quaternion());
    object.quaternion
      .copy(parentQuaternion.invert())
      .multiply(worldQuaternion);
  }
  object.updateMatrixWorld(true);
}

function restoreBindings(
  configurator: SugarModelConfigurator,
  document: RuntimeConfiguratorDocument,
): void {
  const byUuid = new Map(
    configurator.models.map((model) => [model.uuid, model]),
  );
  for (const binding of document.bindings) {
    const first = byUuid
      .get(binding.first.modelUuid)
      ?.joints?.find((joint) => joint.jointId === binding.first.jointId);
    const second = byUuid
      .get(binding.second.modelUuid)
      ?.joints?.find((joint) => joint.jointId === binding.second.jointId);
    if (first && second && first.bindedJoint !== second) {
      first.bindJoint(second);
    }
  }
}
